/**
 * Shared org/stage/route lookup for pipeline stage display components
 * (`PipelineStageValueBadge`, `PipelineStageValueLabel`).
 *
 * Reads the org context optionally: an explicit `orgId` caller may render
 * outside a `TeamProvider` (e.g. an embedded preview), and `useOrg()` throws
 * there. The context is only a fallback for the stage and pipeline-route
 * lookups, so its absence must not crash the caller.
 */

import { useOptionalOrg } from '@/hooks/useOptionalOrg';
import { useStageById } from '@/lib/crm/hooks/useStageById';
import { useStagePipelineRoute } from '@/lib/crm/hooks/useStagePipelineRoute';

import type { Stage } from '../../../hooks/useStages';

export interface UseStageLinkDataOptions {
  /** Optional org ID for stage lookup; falls back to OrgContext internally. */
  orgId?: number;
  /**
   * Whether pipeline-route resolution should run. Callers compute this
   * themselves — e.g. `interactive && Boolean(value)` for a component whose
   * `value` is always a stage entity_id, or additionally gated on UUID
   * validity for a component that also accepts legacy stage_name strings.
   * Passing `false` also skips the pipelines fetch behind `useStagePipelineRoute`.
   */
  enabled?: boolean;
}

/**
 * Look up a stage's data and its pipeline deep link, driven by one effective
 * org id — an explicit `orgId` option wins, falling back to the context org.
 * Using `orgId` alone for the stage lookup (and a separately-computed
 * fallback for the route lookup) let the route resolve while the stage label
 * stayed stuck loading.
 *
 * @example
 * const { stage, pipelineRoute } = useStageLinkData(value, { enabled: interactive && Boolean(value) });
 */
export function useStageLinkData(
  value: string | null | undefined,
  { orgId, enabled = true }: UseStageLinkDataOptions = {}
): {
  stage: Stage | undefined;
  isLoading: boolean;
  hasFailed: boolean;
  pipelineRoute: string | null;
} {
  const orgContext = useOptionalOrg();
  const currentOrg = orgContext?.currentOrg;
  const effectiveOrgId = orgId ?? currentOrg?.id;

  const { stage, isLoading, hasFailed } = useStageById(
    effectiveOrgId,
    value ?? undefined
  );
  const pipelineRoute = useStagePipelineRoute(
    effectiveOrgId,
    value ?? undefined,
    {
      enabled,
    }
  );

  return { stage, isLoading, hasFailed, pipelineRoute };
}
